import { useState } from "react";
import { JsonView } from "react-json-view-lite";

import useClipboard from "../../../utils/hook/useClipboard";

import "./styles.scss";

// Expanded-row layout shared by the tables: labelled fields in a grid, notes,
// sections of items with their actions, and a JSON viewer for nested data.

const isBlank = (value) => value === null || value === undefined || value === "" || value === false;

export const DetailPanel = ({ children, className = "" }) => (
  <div className={`detail-panel ${className}`}>{children}</div>
);

// tone: info, success, warning, danger, muted
export const DetailNote = ({ tone = "info", title, children }) => (
  <p className={`detail-panel__note detail-panel__note--${tone}`}>
    {title && <strong>{title}: </strong>}
    {children}
  </p>
);

export const DetailGrid = ({ columns = 4, children }) => (
  <dl className={`detail-panel__grid detail-panel__grid--${columns}`}>{children}</dl>
);

export const DetailField = ({ label, children, wide = false, mono = false }) => (
  <div className={`detail-panel__field ${wide ? "detail-panel__field--wide" : ""}`}>
    <dt>{label}</dt>
    <dd className={mono ? "detail-panel__mono" : undefined}>
      {isBlank(children) ? "-" : children}
    </dd>
  </div>
);

export const DetailSection = ({ title, count, actions, children }) => (
  <section className="detail-panel__section">
    <header className="detail-panel__section__header">
      <h4>
        {title}
        {count !== undefined && <span className="detail-panel__count">{count}</span>}
      </h4>
      {actions}
    </header>
    {children}
  </section>
);

// One entry of a section (an attachment, a file...) with its buttons on the right
export const DetailItem = ({ children, actions }) => (
  <div className="detail-panel__item">
    <div className="detail-panel__item__body">{children}</div>
    {actions && <div className="detail-panel__item__actions">{actions}</div>}
  </div>
);

export const DetailActions = ({ children }) => (
  <div className="detail-panel__actions">{children}</div>
);

export const DetailCode = ({ children }) => <pre className="detail-panel__code">{children}</pre>;

export const DetailEmpty = ({ children }) => <p className="detail-panel__empty">{children}</p>;

const JSON_STYLE = {
  container: "detail-json__tree",
  basicChildStyle: "detail-json__child",
  expander: "detail-json__expander",
  label: "detail-json__label",
  nullValue: "detail-json__null",
  undefinedValue: "detail-json__null",
  numberValue: "detail-json__number",
  stringValue: "detail-json__string",
  booleanValue: "detail-json__boolean",
  otherValue: "detail-json__other",
  punctuation: "detail-json__punctuation",
  pointer: "detail-json__pointer",
};

// Collapsible JSON tree with expand / collapse all and copy
export const JsonBlock = ({ data, title, initialDepth = 2 }) => {
  const copy = useClipboard();
  // Re-keying the tree is how react-json-view-lite re-applies the initial expansion
  const [view, setView] = useState({ key: 0, expandAll: false });

  return (
    <div className="detail-json">
      <div className="detail-json__toolbar">
        <span className="detail-json__title">{title || "JSON"}</span>
        <button type="button" onClick={() => setView((v) => ({ key: v.key + 1, expandAll: true }))}>
          Expand all
        </button>
        <button
          type="button"
          onClick={() => setView((v) => ({ key: v.key + 1, expandAll: false }))}
        >
          Collapse
        </button>
        <button
          type="button"
          onClick={() => copy(JSON.stringify(data, null, 2), "JSON copied", "Copying JSON failed")}
        >
          Copy
        </button>
      </div>
      <div className="detail-json__body">
        <JsonView
          key={view.key}
          data={data}
          style={JSON_STYLE}
          shouldInitiallyExpand={(level) => view.expandAll || level < initialDepth}
        />
      </div>
    </div>
  );
};

const isScalar = (value) =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

const fieldLabel = (key) => String(key).replace(/[_-]+/g, " ");

const scalarText = (value) => (value === null ? "null" : String(value));

// Values longer than this take a whole row of the field grid
const WIDE_VALUE_LENGTH = 48;

const Titled = ({ title, children }) =>
  title ? (
    <div>
      <span className="detail-panel__record__title">{title}</span>
      {children}
    </div>
  ) : (
    children
  );

// Picks the most readable form: text, a list, a field grid for a flat object,
// and the JSON tree only for nested data
export const DetailValue = ({ value, title }) => {
  if (isScalar(value)) {
    return (
      <Titled title={title}>
        <DetailCode>{scalarText(value)}</DetailCode>
      </Titled>
    );
  }

  if (Array.isArray(value) && value.every(isScalar)) {
    return (
      <Titled title={title}>
        {value.length === 0 ? (
          <DetailEmpty>Empty list</DetailEmpty>
        ) : (
          <ul className="detail-panel__list">
            {value.map((entry, index) => (
              <li key={index}>{scalarText(entry)}</li>
            ))}
          </ul>
        )}
      </Titled>
    );
  }

  if (value && !Array.isArray(value) && typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length && entries.every(([, entry]) => isScalar(entry))) {
      return (
        <div className="detail-panel__record">
          {title && <span className="detail-panel__record__title">{title}</span>}
          <DetailGrid columns={3}>
            {entries.map(([key, entry]) => (
              <DetailField
                key={key}
                label={fieldLabel(key)}
                wide={String(entry).length > WIDE_VALUE_LENGTH}
              >
                {scalarText(entry)}
              </DetailField>
            ))}
          </DetailGrid>
        </div>
      );
    }
  }

  return <JsonBlock data={value} title={title} />;
};
